import {
  Checkbox,
  ChoiceGroup,
  IChoiceGroupOption,
  Icon,
  IProcessedStyleSet,
  IRatingStarProps,
  IRatingStyles,
  IRenderFunction,
  Link,
  PrimaryButton,
  Rating,
  RatingSize,
  Spinner,
  SpinnerSize,
  TextField,
} from '@fluentui/react';
import * as React from 'react';
import { AutomationIds } from '../../../../models/constants/automationIds';
import { AriaRoles } from '../../models/contactUsModels';
import {
  FeedbackSurveyCategory,
  IComponentProps,
  RequestFeedbackSchema,
  RequestFeedbackStarRating,
  RequestFeedbackSubmitStatus,
  SurveyCategoryKeys,
  SurveyCheckBoxCategories,
} from '../../models/feedbackModels';
import { FreelanceRequest } from '../../models/requestModels';
import {
  feedbackCommentTextFieldStylesStyle,
  feedbackSubmitButtonStyles,
  getRequestFeedbackClassNames,
  getSharedStyles,
  getSharedStylesClassNames,
  getSpinnerStyles,
  IRequestFeedbackStyles,
  surveyCategoriesCheckBoxStyle,
} from '../../styles/requestFeedbackStyles';
import { IStyledComponent } from '../../styles/themesUI';
import { getString } from '../../../../../web/utils/stringLoader';

const sharedClassNames = getSharedStylesClassNames(getSharedStyles!, {});

interface IRequestFeedbackComponentProps {
  feedbackCommentMaxLength: number;
  requestId: string;
  feebackSubmitStatus: RequestFeedbackSubmitStatus;
  isProcessing: boolean;
  rating: number;
  ratingText: string;
  allFeedbackDetails: RequestFeedbackSchema;
  surveyCheckBoxes: SurveyCheckBoxCategories;
  feedbackComment: string;
  comment: string;
  feedbackIssueResolved: boolean;
  choiceGroupSelectedKey: string;

  // dispatchers
  dispatchSubmitFeedback: any;
  updateFeedback: any;
  resetFeedback: any;
}

interface IFeedbackPropsCState {
  comment: string;
}

type IRequestFeedbackProps = IComponentProps &
  IRequestFeedbackComponentProps &
  IStyledComponent<IRequestFeedbackStyles>;

export class RequestFeedback extends React.PureComponent<
  IRequestFeedbackProps,
  IFeedbackPropsCState
> {
  public constructor(props: IRequestFeedbackProps) {
    super(props);
    this.state = {
      comment: this.props.feedbackComment,
    };
  }
  public componentDidMount(): void {
    this.props.resetFeedback(this.props.requestId);
  }

  public componentWillUnmount(): void {
    const updatedFeedbackSchema = this.props.allFeedbackDetails;
    updatedFeedbackSchema.Comment = this.state.comment;

    // TODO: Create update reducers for RequestFeedbackSchema properties
    this.props.updateFeedback(updatedFeedbackSchema);
  }

  public render(): React.ReactNode {
    const requestFeedbackClassNames = getRequestFeedbackClassNames(
      this.props.styles,
      { theme: this.props.theme }
    );
    return (
      <>
        {
          <div
            data-automation-id={AutomationIds.RequestFeedback}
            className={requestFeedbackClassNames.feedbackRoot}
          >
            {this.props.feebackSubmitStatus ===
              RequestFeedbackSubmitStatus.None ||
              this.props.feebackSubmitStatus ===
              RequestFeedbackSubmitStatus.Error ? (
              <div className={requestFeedbackClassNames.feedbackLayoutRoot}>
                {!this.props.isProcessing &&
                  this._renderFeedbackForm(requestFeedbackClassNames)}
                {this.props.isProcessing && (
                  <Spinner size={SpinnerSize.large} styles={getSpinnerStyles} />
                )}
              </div>
            ) : (
              this.props.feebackSubmitStatus ===
              RequestFeedbackSubmitStatus.Success &&
              this._getFeedbackSuccessContainer(requestFeedbackClassNames)
            )}
          </div>
        }
      </>
    );
  }

  private _renderFeedbackForm = (
    requestFeedbackClassNames: IProcessedStyleSet<IRequestFeedbackStyles>
  ): React.ReactNode => {
    const toggledFeedbackInteractionStyles =
      this.props.allFeedbackDetails &&
        this.props.allFeedbackDetails.IsFeedbackCategoriesVisible
        ? requestFeedbackClassNames.feedbackInteractionStylesOn
        : requestFeedbackClassNames.feedbackInteractionStylesOff;
    return (
      <div>
        {this._getFeedbackRequestChoiceContainer(requestFeedbackClassNames)}
        {this._getFeedbackRequestRatingContainer(this.props.rating, requestFeedbackClassNames)}
        <div className={toggledFeedbackInteractionStyles}>
          {this._getFeedbackSurveyCategoriesContainer(requestFeedbackClassNames)}
          {this._getFeedbackRequestCommentContainer(requestFeedbackClassNames)}
          {this._getFeedbackRequestFooter(this.props.rating, requestFeedbackClassNames)}
        </div>
      </div>
    );
  };

  private _onSurveyCategoryChange =
    (key: SurveyCategoryKeys) =>
      (
        _ev: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked: boolean
      ): void => {
        const updatedFeedbackSchema = this.props.allFeedbackDetails;
        updatedFeedbackSchema.SurveyCheckBoxes[key] = checked;
        this.props.updateFeedback(updatedFeedbackSchema);
      };

  private _generateSurveyCategoryCheckBoxes = (): React.ReactNode | null => {
    const surveyCheckBoxes = this.props.surveyCheckBoxes;
    if (!!surveyCheckBoxes) {
      const requestId = this.props.requestId;
      let checkBoxes: JSX.Element[] = [];
      // The order of json strings affects the order of UI since we're using display:Flex property to display checkboxes
      checkBoxes = Object.keys(FeedbackSurveyCategory)
        .filter((k) => isNaN(k as any))
        .map((key: SurveyCategoryKeys) => {
          const label: string = getString(`feedbackSurveyCategory_${FeedbackSurveyCategory[key]}`);
          return (
            <Checkbox
              key={requestId + '-' + key}
              label={label}
              checked={surveyCheckBoxes[key]}
              onChange={this._onSurveyCategoryChange(key)}
              styles={surveyCategoriesCheckBoxStyle(this.props.theme)}
            />
          );
        });
      return <div className={sharedClassNames.flexWrap}>{checkBoxes}</div>;
    } else {
      return null;
    }
  };

  private _getFeedbackSurveyCategoriesContainer = (
    requestFeedbackClassNames: IProcessedStyleSet<IRequestFeedbackStyles>
  ): React.ReactNode => {
    return (
      <div
        role={AriaRoles.Group}
        aria-label={getString('requestFeedback_whatImpactedExperienceText')}
      >
        <div className={requestFeedbackClassNames.ratingTitle}>
          {getString('requestFeedback_whatImpactedExperienceText')}
        </div>
        <div>{this._generateSurveyCategoryCheckBoxes()}</div>
      </div>
    );
  };

  private _getFeedbackSuccessContainer = (
    requestFeedbackClassNames: IProcessedStyleSet<IRequestFeedbackStyles>
  ): React.ReactNode => {
    return (
      <div className={requestFeedbackClassNames.feedbackLayoutRoot}>
        <div className={requestFeedbackClassNames.feedbackSuccessRoot}>
          <Icon
            iconName={'SkypeCircleCheck'}
            className={requestFeedbackClassNames.feedbackSuccessIconStyle}
          />
          <div className={requestFeedbackClassNames.feedbackSuccessContent}>
            {getString('requestFeedback_successMessage')}
          </div>
        </div>
      </div>
    );
  };

  private _getFeedbackRequestCommentContainer = (
    requestFeedbackClassNames: IProcessedStyleSet<IRequestFeedbackStyles>
  ): React.ReactNode => {
    return (
      <div className={requestFeedbackClassNames.textFieldContainer}>
        <div
          className={requestFeedbackClassNames.feedbackCommentTextFieldStyles}
        >
          <TextField
            inputClassName={sharedClassNames.maxHeight300}
            placeholder={
              getString('requestFeedback_commentsTextboxPlaceHolderText')
            }
            multiline={true}
            onChange={this._onFeedbackCommentChange}
            maxLength={this.props.feedbackCommentMaxLength}
            value={this.state.comment}
            description={this._getFeedbackCommentTextFieldDescription()}
            styles={feedbackCommentTextFieldStylesStyle(this.props.theme)}
          />
        </div>
      </div>
    );
  };

  private _getFeedbackCommentTextFieldDescription = (): string => {
    if (
      !!this.state.comment &&
      this.state.comment.length > this.props.feedbackCommentMaxLength - 250
    ) {
      return getString('requestFeedback_textFieldCharLimitWarningMessage').replace(
        '{0}',
        (
          this.props.feedbackCommentMaxLength - this.state.comment.length
        ).toString()
      );
    }

    return String();
  };

  private _getFeedbackRequestChoiceContainer = (
    requestFeedbackClassNames: IProcessedStyleSet<IRequestFeedbackStyles>
  ): React.ReactNode => {
    return (
      <div className={sharedClassNames.paddingBottom12}>
        <ChoiceGroup
          options={[
            {
              key: 'true',
              text: getString('requestFeedback_yes'),
              'data-automation-id': 'feedback-satisfaction-yes',
              styles: {
                root: requestFeedbackClassNames.feedbackChoiceRootStyle,
              },
            } as IChoiceGroupOption,
            {
              key: 'false',
              text: getString('requestFeedback_no'),
              'data-automation-id': 'feedback-satisfaction-no',
              styles: {
                root: requestFeedbackClassNames.feedbackChoiceRootStyle,
              },
            } as IChoiceGroupOption,
          ]}
          styles={{
            flexContainer: requestFeedbackClassNames.feedbackChoiceGroupStyles,
            label: requestFeedbackClassNames.ratingTitle,
          }}
          label={getString('requestFeedback_satisfactionQueryText')}
          onChange={this._onFeedbackSatisfactionChange}
          selectedKey={this.props.choiceGroupSelectedKey}
        />
      </div>
    );
  };

  private _getFeedbackRequestRatingContainer = (
    rating: number,
    requestFeedbackClassNames: IProcessedStyleSet<IRequestFeedbackStyles>
  ): React.ReactNode => {
    const renderStar: IRenderFunction<IRatingStarProps> = undefined;
    const ratingStarIsLargeStyle: {} = undefined;
    const rootIsLargeStyle: {} = undefined;
    const ratingFocusZoneStyle = requestFeedbackClassNames.ratingRootStyles;
    const getRatingComponentAriaLabel = (
      rating: number,
      maxRating: number
    ): string => {
      return `Rating value is ${rating} of ${maxRating}`;
    };
    return (
      <div className={sharedClassNames.paddingBottom12}>
        <div className={requestFeedbackClassNames.ratingTitle}>
          {getString('requestFeedback_rateYourExperienceText')}
        </div>
        <div className={requestFeedbackClassNames.ratingComponent}>
          <div className={requestFeedbackClassNames.ratingComponentDiv}>
            <Rating
              data-test-id={'largeRatingStar'}
              min={0}
              max={5}
              size={RatingSize.Large}
              rating={rating}
              onRenderStar={renderStar}
              onChange={this._onRatingChange}
              getAriaLabel={getRatingComponentAriaLabel}
              ariaLabelFormat={
                getString('requestFeedback_rateYourExperienceAriaLabel')
              }
              styles={{
                ratingStarFront:
                  requestFeedbackClassNames.ratingStarFrontStyles,
                ratingFocusZone: ratingFocusZoneStyle,
                ratingStarIsLarge: ratingStarIsLargeStyle,
                rootIsLarge: rootIsLargeStyle,
              }}
            />
          </div>
          {
            <div className={requestFeedbackClassNames.ratingText}>
              {this.props.ratingText}
            </div>
          }
        </div>
      </div>
    );
  };

  private _getFeedbackRequestFooter = (rating: number, requestFeedbackClassNames: IProcessedStyleSet<IRequestFeedbackStyles>): React.ReactNode => {
    return (
      <div className={requestFeedbackClassNames.floatContainer}>
        <div
          className={requestFeedbackClassNames.feedbackSubmitBtnContainerStyles}
        >
          <PrimaryButton
            data-automation-id="feedback-submit"
            allowDisabledFocus={true}
            disabled={rating <= 0}
            text={getString('requestFeedback_submitButtonText')}
            onClick={this._FeedbackSubmit}
            styles={feedbackSubmitButtonStyles(this.props.theme)}
          />
          {this.props.feebackSubmitStatus ===
            RequestFeedbackSubmitStatus.Error && (
              <div
                className={requestFeedbackClassNames.feedbackSubmitErrorStyles}
              >
                {getString('requestFeedback_tryagain')}
              </div>
            )}
        </div>
        <div>
          <Link
            className={requestFeedbackClassNames.feedbackCancelBtnStylesOn}
            onClick={this._onCancelFeedback}
          >
            <span>{getString('requestFeedback_cancel')}</span>
          </Link>
        </div>
      </div>
    );
  };

  private _onCancelFeedback = (_ev: React.MouseEvent<HTMLElement>): void => {
    this.props.resetFeedback(this.props.requestId);
  };

  private _onRatingChange = (
    _ev: React.FocusEvent<HTMLElement>,
    rating: number
  ): void => {
    const FeedbackTitle = this._getFeedbackTitle(rating);
    const updatedFeedbackSchema = this.props.allFeedbackDetails;
    updatedFeedbackSchema.Rating = rating;
    updatedFeedbackSchema.RatingText = FeedbackTitle;
    updatedFeedbackSchema.IsFeedbackCategoriesVisible = true;

    this.props.updateFeedback(updatedFeedbackSchema);
  };

  private _getFeedbackTitle = (rating: number): string => {
    return getString(`requestFeedbackStarRating_${RequestFeedbackStarRating[rating]}`, true, true, true) || '';
  };

  private _onFeedbackCommentChange = (
    _ev: React.FormEvent<HTMLInputElement>,
    newText: string
  ): void => {
    this.setState({ comment: newText });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _onFeedbackSatisfactionChange = (
    _ev: React.FormEvent<HTMLInputElement>,
    option: any
  ): void => {
    const updatedFeedbackSchema = this.props.allFeedbackDetails;
    updatedFeedbackSchema.IssueResolved = JSON.parse(option.key);
    updatedFeedbackSchema.IsFeedbackCategoriesVisible = true;
    this.props.updateFeedback(updatedFeedbackSchema);
  };

  private _FeedbackSubmit = () => {
    const request = new FreelanceRequest();
    const { requestId } = this.props;
    request.RequestId = this.props.requestId;
    request.Rating = this.props.rating.toString();
    request.IsResolved = this.props.feedbackIssueResolved.toString();
    request.Comment = this.state.comment;
    const extensionAttribute = request.ExtensionAttributes;
    extensionAttribute['SurveyType'] = 'star';
    request.ExtensionAttributes = extensionAttribute;
    this.props.dispatchSubmitFeedback(
      request,
      this.props.ratingText,
      requestId,
      this.props.surveyCheckBoxes
    );
  };
}
